/*
 * ```
 *
 *  .  . .  .  . .  .  . .  .  . .  .  . .  .  . .  .  . .  .  . .  .  . .  .  . .  .  . .  .  . .  .
 *     .       .       .       .       .       .       .       .       .       .       .       .
 *       . :SXt. .  :%X%. . ..%S%: . .    . .    .  .    .  .    .  .    .  .    .  .    .  .    .  . .
 *   .   .;8XSXt8 .S8@SX8@  888SS8t.    ..8  %t    .  .      .       .       .   ;X  X:      .
 *     . @%@:8;S8 t8%t8 X8.:88%8.X8X     ;8;:S; .       .  .   . ;;.   .  .    . t8.:X:.t..    .  . .  .
 *    .  8XXS tS8;X88S.SX8::8X; 8X8@ .  .:X;     . .  .   .      t@     .   .   .88    t;8   .   .
 *       8S88:@8@8888S X8888888.8X8@   . @ . . .  ...   .   .. . ;8  .    .   . .8S .  %8: .   .    . .
 *    . .8X88. ;8S%8;.  .8;:8%..8@8@   :S88.S@ ;t@::S S.  @;XttSS88;;;8;;   . %t:@8%:t X8; 8@% :; .
 *       8@@S  .  ...   .. .:. .8X8@ .   S;.:  X8    ;8X t8S     t8   .@;  .88; .8%. . @8.  :8 .8   . .
 *    . .8@@8;  .     .  .     .8X8@    .tX;  ;88.    X@ t8t     t8 .  88S t8@  :8S    @@%   t88.
 *       @@88:     .      .  . .8@8@  . .X .  :8: .  .@S t8@     S@.  . 88%8:.  .8S .  88:  X8%8S.  .  .
 *    .  8X88. .  .   . .   .  .8X8@     X : ..88t%;S@8X %8%  . .:8.     %88t  .:8S   .X8% S8S:X88:
 *      .8XXS    .   .         .8X8@ . ..88.    tXX SS%  :8t     @8. .  .888.   .@S .  tX: 88.. SS:  .
 *    .  8@88:.    .     . .  . 8X8@    .:. .    .: .  . ..  . .  .     S8%. .  .     .    ..       .  .
 *      .@@88:  .     .         8X8@    .     . .   .      .     .  .   SX    .   .      .      .
 *    .  8XX8;    .    .  .  . .8X8X .   . .      .   .  .    .       .88X. .      . . .    .  .  .  .
 *       ;. %  .    .       .  .;:;:.  .     .  .           .   . .  . :;      . .        .         .  .
 *    . . .  .   .   .  .             .   .    .   . . .  .               .  .      .  .    . .  .
 *         .       .      .  . .  .     .   .    .       .   . .  . .  .    .   .        .        .  .
 *    .  .    . .     .    .     .  .     .   .     .  .   .            .  .      . . .    .  . .     .
 *
 *  Copyright FortyFix(c) 2026.
 *  Title: utility.service.ts;
 *  Path:/Volumes/Dati/SbSW_SoftwareBySystem_Works/Fortyfix/Progetti_Angular/src/app/utility.service.ts;
 *  Description: ;
 *  Author: Della Porta Giovanni Battista;
 *  Created at: 28/01/26, 09:50;
 *  Updated at: 28/01/26, 09:50;
 *  ```
 */
import { Injectable } from '@angular/core';
import {environment} from "../environments/environment";
import {Color, hsl} from "d3";
import * as XLSX from "xlsx";
import {DynamicCrudService, OggettoConfig} from "./shared/_services/dynamic-crud.service";
import {CampoAggiuntivo} from "./shared/models/campo-aggiuntivo";

@Injectable({
  providedIn: 'root'
})
export class UtilityService {
  /**
   * Copia nella clipboard una stringa formattata per essere incollata in Word / nDocx.
   *
   * @param content - Il contenuto principale. Se asHtml = true, deve essere HTML valido (es. "<p>...</p>").
   * @param asHtml - Se true prova a copiare anche la versione HTML (migliore per mantenere formattazione in Word). Default: true.
   */
  public static async copyToClipboardForDocx(content: string, asHtml = true): Promise<void> {
    // Normalizziamo plain text (rimuoviamo CRLF strani) — utile per paste corrette
    const plain = content.replace(/\r\n/g, '\n').replace(/\r/g, '\n');

    // Se vogliamo inviare HTML, assicuriamoci di avere un wrapper minimale
    const html = asHtml
      ? (content.startsWith('<') ? content : `<div>${UtilityService.escapeHtml(content).replace(/\n/g, '<br>')}</div>`)
      : '';

    // Metodo moderno: navigator.clipboard.write con ClipboardItem (supporta text/html)
    if (asHtml && typeof navigator !== 'undefined' && 'clipboard' in navigator && (navigator.clipboard as any).write) {
      try {
        const blobPlain = new Blob([plain], { type: 'text/plain' });
        const blobHtml = new Blob([html], { type: 'text/html' });

        // @ts-ignore - ClipboardItem può non essere nel lib dom TS più vecchie
        const item = new ClipboardItem({
          'text/plain': blobPlain,
          'text/html': blobHtml,
        });

        // Scrive entrambi i formati; Word preferirà text/html se presente
        await (navigator.clipboard as any).write([item]);
        return;
      } catch (err) {
        // fallthrough al fallback
        // console.warn('ClipboardItem write failed, falling back to writeText', err);
      }
    }

    // Fallback 1: navigator.clipboard.writeText (solo testo)
    if (typeof navigator !== 'undefined' && 'clipboard' in navigator && typeof navigator.clipboard.writeText === 'function') {
      try {
        await navigator.clipboard.writeText(plain);
        return;
      } catch (err) {
        // fallthrough
        // console.warn('writeText failed, trying execCommand fallback', err);
      }
    }

    // Fallback 2: textarea + execCommand (funziona in molti browser non secure)
    try {
      const ta = document.createElement('textarea');
      ta.value = plain;
      // rendiamo invisibile ma selezionabile
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      ta.setAttribute('aria-hidden', 'true');
      document.body.appendChild(ta);
      ta.select();
      ta.setSelectionRange(0, ta.value.length);

      const ok = document.execCommand('copy');
      document.body.removeChild(ta);

      if (!ok) throw new Error('execCommand returned false');
      return;
    } catch (err) {
      throw new Error('Impossibile copiare negli appunti: nessuna API disponibile o permesso negato.');
    }
  }

  /** Escape molto semplice per inserire testo in HTML quando user passa asHtml=false ma vogliamo wrapper */
  public static escapeHtml(s: string) {
    return s.replace(/[&<>"']/g, (c) => {
      switch (c) {
        case '&': return '&amp;';
        case '<': return '&lt;';
        case '>': return '&gt;';
        case '"': return '&quot;';
        case "'": return '&#39;';
        default: return c;
      }
    });
  }

  static includePath(strings: string[]) {
    return strings.some(string => location.origin.includes(string))
  }

  public static getblankRedirectUrl(url?:string){
    const route = `${UtilityService.getUrlSubfolder()}/${url}`;
    return route;
  }
  public static getUrlSubfolder(){
    const splittedApiUrl = environment.apiUrl.split('/');
    const exceptions = ['localhost', '192.168', '84.247'];
    return (environment['baseHref'] || splittedApiUrl.length > 4) && !UtilityService.includePath(exceptions) ? `/${(environment['baseHref'] ?? splittedApiUrl[3])}` : '';
  }

  public static LightenColor(color: Color, percent = 50) {
    var num = parseInt(color.toString().replace("#", ""), 16);
    if (num < 16750000)
      return hsl(0, 0, 100);
    else
      return hsl(0, 0, 0);
  };



  public static Export(items: any[],  type: "csv" | "xlsx",PrintHeaders: CampoAggiuntivo[], FileName: string, completo?:boolean, totalItems?: number, size?: number, fetch?: (page: number, size: number) => Promise<any[]>,){
    if(completo && totalItems > size ){
      (async () => {
        const allItems = await UtilityService.getAll(fetch,size, totalItems);
        UtilityService.Download(allItems, type, PrintHeaders, FileName);
      })();
    }
    else if(items.some(x => x['selected'])){
      UtilityService.Download(items.filter(x => x['selected']), type,  PrintHeaders, FileName);
    } else
      UtilityService.Download(items, type,  PrintHeaders, FileName);
  }
  private static Download(items: any[], type: "csv" | "xlsx", PrintHeaders: CampoAggiuntivo[], FileName: string){
    switch (type) {
      case "csv":
        UtilityService.DownloadCsv(items, {PrintHeaders, Id:'Exportexcel'}, FileName, {});
        break;
      case "xlsx":
        UtilityService.Downloadxlsx(items, {PrintHeaders, Id:'Exportexcel'}, FileName, {});
        break;
    }
  }


  private static DownloadCsv(List: any[], Settings: OggettoConfig, filename = 'Export', DropdownSources) {
    const Headers = Settings.PrintHeaders?.filter(header => header.printable != false) ?? Settings.Headers?.filter(header => header.printable != false);
    const normalizedData: any[] = List.map(element => {
      var newElement = {};
      Headers.forEach(header => {
        newElement[header.nome] = DynamicCrudService.getCampoAsString(header, element, DropdownSources) ?? '';
      })
      return newElement;
    })
    console.log('Generated Data: ', { data: normalizedData, headers: Headers.map(x => x.nome) });
    const csvData = UtilityService.ConvertToCSV(normalizedData, Headers.map(x => x.nome));
    console.log(csvData);
    let blob = new Blob(['\ufeff' + csvData], { type: 'text/csv;charset=utf-8;' });
    let dwldLink = document.createElement("a");
    let url = URL.createObjectURL(blob);
    let isSafariBrowser = navigator.userAgent.indexOf('Safari') != -1 && navigator.userAgent.indexOf('Chrome') == -1;
    if (isSafariBrowser) {  //if Safari open in new window to save file with random filename.
      dwldLink.setAttribute("target", "_blank");
    }
    dwldLink.setAttribute("href", url);
    dwldLink.setAttribute("download", filename + ".csv");
    dwldLink.style.visibility = "hidden";
    document.body.appendChild(dwldLink);
    dwldLink.click();
    document.body.removeChild(dwldLink);
  }
  public static ConvertToCSV(objArray, headerList) {
    let array = typeof objArray != 'object' ? JSON.parse(objArray) : objArray;
    let str = '';
    let row = '';
    for (let index in headerList) {
      row += headerList[index] + ';';
    }
    row = row.slice(0, -1);
    str += row + '\r\n';
    for (let i = 0; i < array.length; i++) {
      let line = '';
      for (let index in headerList) {
        let head = headerList[index];
        line += array[i][head] + ';';
      }
      str += line + '\r\n';
    }
    return str;
  }

  private static Downloadxlsx(List: any[], Settings: OggettoConfig, filename = 'Export', DropdownSources) {
    const Headers = Settings.PrintHeaders?.filter(header => header.printable != false) ?? Settings.Headers?.filter(header => header.printable != false);
    const normalizedData: any[] = List.map(element => {
      var newElement = {};
      Headers.forEach(header => {
        newElement[header.nome] = DynamicCrudService.getCampoAsString(header, element, DropdownSources) ?? '';
      })
      return newElement;
    })
    UtilityService.exportToExcel(normalizedData, Headers, filename);
  }



  static exportToExcel(normalizedData: any[], Headers: any[], filename: string) {
    // 1. Prepara i dati per Excel
    const headerNames = Headers.map(h => h.nome);
    const excelData = [headerNames];

    for (const row of normalizedData) {
      const rowData = headerNames.map(h => row[h] ?? '');
      excelData.push(rowData);
    }

    // 2. Crea worksheet e workbook
    const worksheet: XLSX.WorkSheet = XLSX.utils.aoa_to_sheet(excelData);
    const workbook: XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Foglio1');

    // 3. Genera Blob
    const xlsxBuffer: any = XLSX.write(workbook, {
      bookType: 'xlsx',
      type: 'array'
    });
    const blob = new Blob([xlsxBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });

    // 4. Download manuale (compatibile con Safari, Chrome, etc)
    const dwldLink = document.createElement("a");
    const url = URL.createObjectURL(blob);
    const isSafari = navigator.userAgent.includes("Safari") && !navigator.userAgent.includes("Chrome");

    if (isSafari) {
      dwldLink.setAttribute("target", "_blank");
    }

    dwldLink.setAttribute("href", url);
    dwldLink.setAttribute("download", `${filename}.xlsx`);
    dwldLink.style.visibility = "hidden";
    document.body.appendChild(dwldLink);
    dwldLink.click();
    document.body.removeChild(dwldLink);
  }

  public static  async  getAll(
    fetch: (page: number, size: number) => Promise<any[]>,
    size: number,
    totalItems: number,
    page: number = 0,
    acc: any[] = []
  ): Promise<any[]> {

    const totalPages = Math.ceil(totalItems / size);

    // condizione di uscita
    if (page >= totalPages) {
      return acc;
    }



    // chiamata API
    const data = await fetch(page, size);

    // ricorsione
    return UtilityService.getAll(
      fetch,
      size,
      totalItems,
      page + 1,
      [...acc, ...data]
    );
  }


}

